/**
 * 재계약 생성 모달
 * 원계약을 복사해 같은 계약관리 하위에 '신청확인' 상태의 계약을 생성한다.
 * 상품과 계약조건은 Core에서 원계약을 승계하므로 계약 종료일만 입력받는다.
 */

import moment from "moment";
import { useMemo, useState } from "react";
import { renewContract_RCA01 } from "src/api/contract/contract-api";
import { DetailTotalData } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton, BaseDatePicker } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import BaseSection from "src/components/layout/BaseSection";
import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import { ViewYmdFormat } from "src/utils";
import { getRenewalEndTime, getRenewalStartTime, hasPeriodOverlap } from "../../hooks/useContractRenewal";

type Props = {
  isOpen: boolean;
  detailData: DetailTotalData; // 원계약 상세 정보
  onClose: () => void;
};

const ContractRenewalModal = ({ isOpen, detailData, onClose }: Props) => {
  const navigate = useNavigate();
  const { executeAsync: renewContract } = useApiOperation(renewContract_RCA01);

  const [endDate, setEndDate] = useState<Date | null>(null);
  const [errorText, setErrorText] = useState("");

  const { contract, product } = detailData;

  // 재계약 계약 시작일 = 원계약 종료일 다음날 자정
  const startTime = useMemo(() => getRenewalStartTime(contract?.contractEndTime), [contract?.contractEndTime]);

  const onClickCreate = async () => {
    if (!endDate) {
      setErrorText("계약 종료일을 선택해 주세요.");
      return;
    }

    const useEndTime = getRenewalEndTime(endDate);

    // 시작일이 원계약 종료일 다음날로 고정이라 정상 흐름에서는 겹칠 수 없지만,
    // BE에 기간 중첩 검사가 없으므로 생성 직전 마지막으로 확인한다
    if (hasPeriodOverlap(startTime, useEndTime, detailData?.contracts)) {
      setErrorText("기존 계약과 계약 기간이 겹칩니다. 계약 종료일을 다시 선택해 주세요.");
      return;
    }

    const response = await renewContract({
      originContractId: Number(contract?.contractId),
      applicationBody: {
        useStartTime: startTime,
        useEndTime,
        isAgreeTerms: true,
      },
    });

    const createdContractId = response?.data?.data?.contractId;
    if (!createdContractId) return;

    onClose();
    navigate(PagePath.contract.detail.replace(":id", String(createdContractId)));
  };

  return (
    <BaseAbstractModal isOpen={isOpen} size="large">
      <section className="base-abstract-modal__title has-description border-bottom">
        <p>{contract?.contractName}</p>
        <h1>재계약을 생성하시겠습니까?</h1>
      </section>

      <section className="base-abstract-modal__contents px30">
        <section className="base-abstract-modal__contents-subtitle">
          <h2>기본 정보</h2>
        </section>
        <BaseSection
          title={"상품명"}
          children={
            <div className="flex-row flex-center-start w-100 font14">
              <p className="mr5">{product?.productName || "-"}</p>
              <div className="flex-center no-wrap">
                (id: {product?.id && <BaseNewTabLink path={PagePath.product.detail.replace(":id", String(product.id))} value={String(product.id)} />})
              </div>
            </div>
          }
        />
        <ul className="base-list">
          <li>상품은 원계약과 동일하게 유지됩니다.</li>
        </ul>

        <section className="base-abstract-modal__contents-subtitle">
          <h2>계약 기간</h2>
        </section>
        <BaseSection title={"계약 시작일"} value={moment(startTime).format(ViewYmdFormat.YYYY_MM_DD)} />
        <BaseSection
          title={"계약 종료일"}
          children={
            <BaseDatePicker
              selectedDate={endDate}
              setDate={(date: Date | null) => {
                setEndDate(date);
                setErrorText("");
              }}
              className="minmax160"
              minDate={moment(startTime).toDate()}
              placeholder="날짜를 선택해 주세요"
              errorText={errorText}
            />
          }
        />
        <ul className="base-list">
          <li>계약 시작일은 원계약 종료일 다음 날로 자동 설정됩니다.</li>
        </ul>
      </section>

      <section className="base-abstract-modal__btn-wrap">
        <BaseButton title={"취소"} className="color-white" onClick={onClose} />
        <BaseButton title={"생성"} onClick={onClickCreate} />
      </section>
    </BaseAbstractModal>
  );
};

export default ContractRenewalModal;
